import { useMemo, useState } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, ArrowCycle, Download } from '@factorialco/f0-react/icons/app'
import { ExpensablesDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'
import { usePageDateFilters } from 'modules/expenses/lib/PageDateFiltersContext'
import { type RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { useExpenses } from 'modules/expenses/pages/ExpensesContext'
import { Modals as SyncModal } from 'modules/integrations/components/SyncButton/Modals'
import { useSync } from 'modules/integrations/hooks/useSync'

import { ExpensesDataCollectionInsights } from './ExpensesInsights'
import { mapFiltersToVariables } from './_Filters'
import { useExpensesBulkActions } from './bulkActions'
import { useExpenseItemActions } from './itemActions'
import { useExpensesDataCollection } from './useExpensesDataCollection'
import { useExpensesFilters } from './useExpensesFilters'
import { useExpensesInsightsFilters } from './useExpensesInsightsFilters'

interface Props {
  companyId: number
  canSeeTransactions: boolean
  currentPayrollPeriodOpen: boolean
  availablePayrollCategories: string[]
  back: { href: string }
  onAddUsers?: () => void
}

export const ExpensesDataCollection = ({
  companyId,
  canSeeTransactions,
  currentPayrollPeriodOpen,
  availablePayrollCategories,
  back,
  onAddUsers,
}: Props) => {
  const context = useOutletContext<RouteContextParams>()
  const {
    employee,
    access: { locale },
  } = useCurrentSession()

  const navigate = useNavigate()
  const { navigation } = useExpensesNavigation()
  const { data: expensesData } = useExpenses()

  const { dateFilters, setPageDateFilters } = usePageDateFilters()
  const { setActiveFilters, getInsightsFilters } = useExpensesInsightsFilters()
  const filterDefinitions = useExpensesFilters({ companyId })

  const { sortings, defaultSortings, columns } = useExpensesDataCollection({ locale })
  const expensePermissions = useMemo(
    () => ({
      canSyncWithPayroll: context.canSyncWithPayroll,
      canSyncWithBanking: context.canSyncWithBanking,
      canRestartApprovalFlow: expensesData.expenses._policies.expenseRestartApprovalFlow.can,
      canSetExpensableToPaid: expensesData.expenses._policies.expenseSetToPaid.can,
      section: context.section,
    }),
    [context, expensesData.expenses._policies]
  )

  const { bulkActions, handleBulkAction, Modals, handlers, modalActions } = useExpensesBulkActions({
    ...expensePermissions,
    currentPayrollPeriodOpen,
    availablePayrollCategories,
  })

  const { getItemActions } = useExpenseItemActions({
    ...expensePermissions,
    handlers,
    modalActions,
  })

  const [showSyncModal, setShowSyncModal] = useState(false)
  const { syncEnabled, marketplaceIntegration } = useSync()
  const isSyncEnabled = syncEnabled && marketplaceIntegration

  const dataSource = useGraphqlDataCollectionSource({
    document: ExpensablesDataCollectionDocument,
    mapData: (data) => data.expenses.expensablesConnection.nodes,

    mapVariables: ({ navigationFilters, search, sortings: mapSortings, filters }) => {
      const { status, employeeIds, filter } = mapFiltersToVariables(filters)

      return {
        companyId: companyId.toString(),
        sortOrder: mapSortings?.[0],
        includeManualDrafts: false,
        search,
        from: navigationFilters?.date?.value?.from,
        to: navigationFilters?.date?.value?.to,
        status,
        employeeIds,
        filter,
      }
    },

    paginationType: 'infinite-scroll',
    perPage: 100, // TODO: Change when F0 fixes the issue with the infinite scroll https://github.com/factorialco/f0/pull/2839
    mapPagination: (data) => ({
      total: data.expenses.expensablesConnection.totalCount ?? 0,
      cursor: data.expenses.expensablesConnection.pageInfo.endCursor ?? undefined,
      hasNextPage: data.expenses.expensablesConnection.pageInfo.hasNextPage ?? false,
    }),

    navigationFilters: {
      date: {
        defaultGranularity: 'range',
        defaultValue: {
          from: dateFilters.startDate,
          to: dateFilters.endDate,
        },
        granularity: ['day', 'week', 'month', 'year', 'range'],
        type: 'date-navigator',
      },
    },
    search: {
      enabled: true,
    },
    filters: filterDefinitions,
    presets: [
      {
        label: i18n.t('expenses_v2.expense.status.pending'),
        filter: { status: ['pending'] },
      },
      {
        label: i18n.t('expenses_v2.expense.status.approved'),
        filter: { status: ['approved'] },
      },
      {
        label: i18n.t('expenses_v2.expense.status.paid'),
        filter: { status: ['paid'] },
      },
    ],
    sortings,
    defaultSortings,
    itemUrl: ({ id }) => navigation.helpers.expensableDetail(id),
    itemActions: getItemActions,
    primaryActions: () => [
      ...(isSyncEnabled
        ? [
            {
              label: i18n.t('integrations.sync_action', {
                integration_name: marketplaceIntegration.name,
              }),
              icon: ArrowCycle,
              onClick: () => {
                setShowSyncModal(true)
              },
            },
          ]
        : []),
      {
        label: i18n.t('expenses_v2.buttons.export_expenses'),
        icon: Download,
        onClick: () => {
          navigate(navigation.exporter.fullPath())
        },
      },
    ],
    secondaryActions: {
      expanded: 1,
      actions: () => [
        ...(onAddUsers
          ? [
              {
                label: i18n.t('billing.subscription.plans.expenses.actions.add_users'),
                icon: Add,
                onClick: onAddUsers,
              },
            ]
          : []),
      ],
    },
    selectable: (item) => item.id,
    bulkActions,
  })

  return (
    <>
      <Modals />
      {showSyncModal && <SyncModal setShowModal={setShowSyncModal} />}
      <Outlet context={{ ...context, back, canSeeTransactions, dataCollectionEnabled: true }} />
      <Box paddingX="s4">
        <ExpensesDataCollectionInsights
          getFiltersVariables={getInsightsFilters}
          locale={locale}
          employee={employee}
          dateFilters={dateFilters}
        />
      </Box>
      <Box paddingX="s12" style={{ height: '100%', overflow: 'auto' }}>
        <OneDataCollection
          id="expenses/data-collection/v1"
          storage={{
            features: ['filters', 'sortings', 'search'],
          }}
          fullHeight
          emptyStates={{
            'no-data': {
              title: i18n.t('expenses_v2.list.empty_state.no_expenses'),
              description: '',
            },
          }}
          source={dataSource}
          onBulkAction={handleBulkAction}
          onStateChange={(state) => {
            // Sync date filters with page-level state
            const { from, to } = state.navigationFilters?.date?.value || {}
            if (from && to) {
              if (
                dateFilters.startDate.getTime() !== from.getTime() ||
                dateFilters.endDate.getTime() !== to.getTime()
              ) {
                setPageDateFilters(from, to)
              }
            }

            // Sync active filters with insights
            setActiveFilters(state.filters)
          }}
          visualizations={[
            {
              type: 'table',
              options: {
                allowColumnHiding: true,
                allowColumnReordering: true,
                columns,
              },
            },
          ]}
        />
      </Box>
    </>
  )
}
